import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { catchError, lastValueFrom, map, Observable, of } from 'rxjs';
import { Session, SessionMapper } from '../../models/session';
import { CreditPlan, CreditPlanMapper } from '../../models/credit-plan';
import { OPE_AUTHORIZATION_CREATE, OPE_FLAGS_GET, OPE_SESSION_CLOSE, OPE_SESSION_GET } from '../../constants/endpoints';
import { handleGenericHttpError } from '../../utils/http-error.util';
import { SessionRequest } from '../../dto/session-request';
import { ApiResponse } from '../../dto/api-response';
import { StoreService } from '../store/store.service';
import { SessionActionRequest } from '../../dto/session-action-request';
import { AuthorizationCreateResponse } from '../../dto/authorization-create-response';
import { FlagsResponse } from '../../dto/flags-response';

interface SessionInfo {
  session: Session;
  creditPlan: CreditPlan | null;
}

/**
 * SessionService handles all operation tangentially related to sessions. This
 * includes handling logic with sessions and authorizations
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  // Injected dependencies
  private http = inject(HttpClient);
  private store = inject(StoreService);

  // Settable signals
  private hasErrorSig = signal<string | null>(null);
  public hasError = this.hasErrorSig.asReadonly();

  // One-time fetch flags
  private fetchedSession = signal<boolean>(false);
  private fetchedFlags = signal<boolean>(false);

  constructor() {
    // Effect to fetch session info when sessionId changes
    effect(() => {
      const sessionId = this.store.sessionId();
      
      // Return early if no session id or session was already fetched
      if (!sessionId || sessionId.trim() === '') return;

      // Return early if already fetched
      if (this.fetchedSession()) return;

      // Fetch session information
      untracked(() => this.fetchedSession.set(true));
      this._fetchSessionInfo(sessionId).subscribe();
    });

    // Effect to fetch flags when token or sessionId changes
    effect(() => {
      const flags = this.store.flags();
      const token = this.store.token();
      const sessionId = this.store.sessionId();

      // Don't fetch if flags have:
      // - preapproval flow set (onlyShowApplication)
      // - not enough credit (from user applying)
      if (flags?.onlyShowApplication || flags?.notEnoughCredit) return;
      
      // Return early if no token
      if (!token || token.trim() === "") return;

      // Throw error if token exists but no session ID
      if (!sessionId || sessionId.trim() === "") {
        throw new Error("token is valid but session id is undefined");
      }

      // Return early if already fetched
      if (this.fetchedFlags()) return;

      // Fetch the flags
      untracked(() => this.fetchedFlags.set(true));
      this._fetchFlags(sessionId).subscribe();
    });
  }

  /**
   * Fetch transaction details about a session
   * @param sessionId GUID of session to fetch
   */
  private _fetchSessionInfo(sessionId: string): Observable<SessionInfo | null> {
    if (!sessionId || sessionId.trim() === "") throw new Error("session id cannot be empty");

    return this.http.get<ApiResponse<SessionRequest>>(OPE_SESSION_GET(sessionId)).pipe(
      map(r => {
        const data: SessionRequest = r.data;

        // Construct models from request
        const session = SessionMapper.toSession(data);
        const creditPlan = CreditPlanMapper.toCreditPlan(data);

        // Update store
        this.store.setSession(session);
        this.store.setCreditPlan(creditPlan);
        this.store.setFlag('onlyShowApplication', data.onlyShowApplication);

        return { session, creditPlan };
      }),
      catchError((err) => {
        console.error('[SESSION-SERVICE]: unable to load session info', err);

        const msg = err.error?.detail || "Unable to load session information";
        this.hasErrorSig.set(msg);
        return of(null);
      }),
    );
  }

  /**
   * Fetch specific flags for a borrower and session
   * @param sessionId GUID of session to fetch
   */
  private _fetchFlags(sessionId: string): Observable<FlagsResponse | null> {
    if (!sessionId || sessionId.trim() === "") throw new Error("session id cannot be empty");

    return this.http.get<ApiResponse<FlagsResponse>>(OPE_FLAGS_GET(sessionId)).pipe(
      map(r => {
        const data: FlagsResponse = r.data;

        // Update store with flags
        this.store.setFlag('notEnoughCredit', data.notEnoughCredit);
        this.store.setFlag('accountPastDue', data.accountPastDue);
        this.store.setFlag('closedCreditLine', data.closedCreditLine);

        return data;
      }),
      catchError((err) => {
        console.error('[SESSION-SERVICE]: unable to load flags', err);

        const msg = err.error?.detail || "Unable to load flags";
        this.hasErrorSig.set(msg);
        return of(null);
      }),
    );
  }

  /**
   * Accept a session to form an authorization
   * @param sessionId Session GUID to accept
   * @return Accepted authorization details
   */
  public acceptSession(sessionId: string): Observable<AuthorizationCreateResponse> {
    if (!sessionId || sessionId.trim() === "") throw new Error("session id cannot be empty");

    const body: SessionActionRequest = {sessionId};
    return this.http.post<ApiResponse<AuthorizationCreateResponse>>(OPE_AUTHORIZATION_CREATE, body).pipe(
      map(r => {
        return r.data;
      }),
      catchError(handleGenericHttpError),
    );
  }

  /**
   * Close a session
   * @param sessionId Session GUID to close
   * @return Observable on successful close
   */
  public closeModalAndSession(sessionId: string): Observable<void> {
    if (!sessionId || sessionId.trim() === "") throw new Error("session id cannot be empty");

    const body: SessionActionRequest = {sessionId};
    return this.http.post<ApiResponse<{message: string}>>(OPE_SESSION_CLOSE, body).pipe(
      map(r => {
        return;
      }),
      catchError(handleGenericHttpError),
    );
  }
}
